import React, { useState, useMemo, useCallback, useEffect, useContext } from 'react';
import * as PropTypes from 'prop-types';

import Tooltip from '@components/tooltip';

import styles from './styles.modules.scss';
import i18nStrings from './strings';
import { useLocalized } from 'utils/useLocalized';
import { DataContext } from 'contexts';
import getUrlParameter from 'ati-utils/getUrlParameter';
import { urlParams } from 'logic/entities';
import Slider from '@components/slider';

const periodIndexes = {
    '10min': 0,
    '1h': 1,
    '1d': 2,
    '1w': 3,
    '1m': 4,
    '3m': 5,
    '6m': 6,
    '1y': 7
};

const PeriodSlider = ({locked, onChange}) => {
    const strings = useLocalized(i18nStrings);
    const { licenseInfo, isOutsideRussia } = useContext(DataContext);
    const [selectedIndex, setSelectedIndex] = useState(periodIndexes['1m']);

    const handleChangePeriod = useCallback((index) => {
	    setSelectedIndex(index);
        let beginDate = new Date();
        let title = '';
        let period = '';
        switch (index) {
            case periodIndexes['10min']: {
                beginDate.setMinutes(beginDate.getMinutes() - 10);
                title = strings.period.minutes.full;
	            period = '10min';
                break;
            }
            case periodIndexes['1h']: {
                beginDate.setHours(beginDate.getHours() - 1);
                title = strings.period.hour.full;
	            period = '1h';
                break;
            }
            case periodIndexes['1d']: {
                beginDate.setDate(beginDate.getDate() - 1);
                title = strings.period.day.full;
	            period = '1d';
                break;
            }
            case periodIndexes['1w']: {
                beginDate.setDate(beginDate.getDate() - 7);
                title = strings.period.week.full;
	            period = '1w';
                break;
            }
            case periodIndexes['1m']: {
                beginDate.setMonth(beginDate.getMonth() - 1);
                title = strings.period.month.full;
	            period = '1m';
                break;
            }
            case periodIndexes['3m']: {
                beginDate.setMonth(beginDate.getMonth() - 3);
                title = strings.period.quarter.full;
	            period = '3m';
                break;
            }
            case periodIndexes['6m']: {
                beginDate.setMonth(beginDate.getMonth() - 6);
                title = strings.period.halfYear.full;
	            period = '6m';
                break;
            }
            case periodIndexes['1y']: {
                beginDate.setFullYear(beginDate.getFullYear() - 1);
                title = strings.period.year.full;
	            period = '1y';
                break;
            }
        }

        onChange(beginDate.toISOString(), period, title);
    }, [onChange, strings]);

    const sliderJSX = useMemo(() => (
        <Slider
	        //порядок элементов в options важен, тк завязываемся на index
            options={[
	            {
		            value: strings.period.minutes.full,
		            shortValue: strings.period.minutes.short,
	            },
	            {
		            value: strings.period.hour.full,
		            shortValue: strings.period.hour.short,
	            },
	            {
		            value: strings.period.day.full,
		            shortValue: strings.period.day.short,
	            },
	            {
		            value: strings.period.week.full,
		            shortValue: strings.period.week.short,
	            },
	            {
		            value: strings.period.month.full,
		            shortValue: strings.period.month.short,
	            },
	            {
		            value: strings.period.quarter.full,
		            shortValue: strings.period.quarter.short,
	            },
	            {
		            value: strings.period.halfYear.full,
		            shortValue: strings.period.halfYear.short,
	            },
	            {
		            value: strings.period.year.full,
		            shortValue: strings.period.year.short,
	            },
            ]}
            selectedIndex={selectedIndex}
            onChange={handleChangePeriod}
        />
    ), [ strings, selectedIndex, handleChangePeriod]);

    const initPeriodValue = useCallback(() => {
        if ((!licenseInfo || !licenseInfo.searchAccessFinishUtc) && !isOutsideRussia) {
            handleChangePeriod(periodIndexes['1m']);
            return;
        }
        const period = getUrlParameter(urlParams.period);
        if (period) {
            if (periodIndexes[period] !== undefined) {
                handleChangePeriod(periodIndexes[period]);
                return;
            }
        }
        handleChangePeriod(periodIndexes['1m']);
    }, [licenseInfo, isOutsideRussia, handleChangePeriod]);

    useEffect(() => {
        initPeriodValue();
    }, [initPeriodValue]);

    return (
        <div className={styles['element']}>
            <div className={styles['title']}>
	            {strings.title}
            </div>
            {!locked ? sliderJSX : (
                <Tooltip
                    content={strings.lockTooltip}
                    maxWidth={230}
                >
                    <div className={styles['locked']}>
                        {sliderJSX}
                    </div>
                </Tooltip>
            )}
        </div>
    );
};

export default PeriodSlider;

PeriodSlider.propTypes = {
    locked: PropTypes.bool,
    onChange: PropTypes.func
};
